import { useContext, useEffect, useRef } from "react";
import { Image, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import AntdTable from "../../../antd/Table/Table";
import { filtrationcontext } from "../../../../context/Filtercontext";
import { exportContext } from "../../../../context/ExportDataContext";
import trans, { isRTL } from "../../../../utils";
import {
  useLazyGetShelfAvailabilityStoresQuery,
  useLazyGetShelfAvailabilityRepsQuery,
  useLazyGetShelfAvailabilityProductsQuery,
} from "../../../../Redux/service/NewReports/ShelfAvailability";


const FORM_ID = process.env.REACT_APP_SHELF_FORM_ID;

const STATE_COLORS = { A: "green", B: "blue", C: "orange", D: "red" };

const ShelfAvailabilityReportTable = ({
  data,
  isLoading,
  currentPage,
  setCurrentPage,
  filters,
  setFilters,
  startDate,
  endDate,
}) => {
const { setSort: SetSortFilter, Sort, limit, setLimit } = useContext(filtrationcontext);
  const { setExportedDataHeader, setExportedDataBody, setFileName, setStartDateE, setEndDateE, setFilters: setExportFilters } =
    useContext(exportContext);

  const [triggerStores, { data: storesData }] = useLazyGetShelfAvailabilityStoresQuery();
  const [triggerReps, { data: repsData }] = useLazyGetShelfAvailabilityRepsQuery();
  const [triggerProducts, { data: productsData }] = useLazyGetShelfAvailabilityProductsQuery();

  const fetchedFilters = useRef(false);

  useEffect(() => {
    if (FORM_ID && !fetchedFilters.current) {
      fetchedFilters.current = true;
      triggerStores({ formId: FORM_ID });
      triggerReps({ formId: FORM_ID });
      triggerProducts({ formId: FORM_ID });
    }
  }, [triggerStores, triggerReps, triggerProducts]);

  useEffect(() => {
    return () => {
      SetSortFilter({ by: "", order: "" });
    };
  }, [SetSortFilter]);

  // Keep export context in sync with current data
  useEffect(() => {
    const items = data?.items || [];
    const headers = [
      trans("Date"),
      trans("Store"),
      trans("State"),
      trans("City"),
      trans("Sales Rep"),
      trans("SKU"),
      trans("Category"),
      trans("Location Type"),
      trans("Case Pack"),
      trans("State1"),
      trans("Shelf Status"),
      trans("Shelf Qty"),
      trans("Containers"),
      trans("Shelf Price"),
      trans("Warehouse Status"),
      trans("Warehouse Qty"),
      trans("Photo Before"),
      trans("Photo After"),
      trans("Notes"),
    ];
    const rows = items.map((row) => [
      row.date ? dayjs(row.date).format("YYYY-MM-DD HH:mm") : "",
      row.store || "",
      row.stateGeo || "",
      row.city || "",
      row.rep || "",
      row.sku || "",
      row.category ? (isRTL ? row.category.name : (row.category.nameEn || row.category.name)) : "",
      row.storeType ? trans(row.storeType) : "",
      row.unitsPerContainer ?? "",
      row.state || "",
      row.shelfStatus || "",
      row.shelfQty ?? "",
      row.shelfQtyContainers ?? "",
      row.shelfPrice ?? "",
      row.warehouseStatus || "",
      row.warehouseQty ?? "",
      row.photoBeforeUrl || "",
      row.photoAfterUrl || "",
      row.notes || "",
    ]);
    setFileName("Shelf Availability Report");
    setExportedDataHeader(headers);
    setExportedDataBody(rows);
    setStartDateE(startDate || "");
    setEndDateE(endDate || "");
    // Expose the active filters so the backend export request matches the report
setExportFilters({
  storeId:         filters.storeId,
  repId:           filters.repId,
  sku:             filters.sku,
  states:          filters.states,
  tableStoreTypes: filters.tableStoreTypes,
  tableCategories: filters.tableCategories,
  tableStates:     filters.tableStates,
  tableCities:     filters.tableCities,
  sortBy:          Sort.by,      // add this
  sortOrder:       Sort.order,   // add this
});
  }, [data, startDate, endDate, filters, Sort, setFileName, setExportedDataHeader, setExportedDataBody, setStartDateE, setEndDateE, setExportFilters]);

  const storeOptions   = (storesData?.data   || []).map((s) => ({ text: s.name,  value: s.id }));
  const repOptions     = (repsData?.data     || []).map((r) => ({ text: r.name,  value: r.id }));
  const productOptions = (productsData?.data || []).map((p) => ({ text: p.label, value: p.value }));

  const storeTypeOptions = (data?.filterOptions?.storeTypes || []).map(t => ({ text: trans(t.value), value: t.value }));
  const categoryOptions  = (data?.filterOptions?.categories || []).map(c => ({ text: isRTL ? c.name : (c.nameEn || c.name), value: c.id }));
  const stateOptions     = (data?.filterOptions?.states || []).map(s => ({ text: trans(s.value), value: s.value }));

  const tableStoreTypes = filters.tableStoreTypes || null;
  const tableCategories = filters.tableCategories || null;
  const tableStates     = filters.tableStates || null;
  const tableCities     = filters.tableCities || null;

  // City options cascade off the selected state(s): each city option carries its
  // state, so when a state is chosen we only offer that state's cities.
  const allCityOptions = data?.filterOptions?.cities || [];
  const cityOptions = (
    tableStates && tableStates.length
      ? allCityOptions.filter(c => tableStates.includes(c.state))
      : allCityOptions
  ).map(c => ({ text: trans(c.value), value: c.value }));

  const columns = [
    {
      title: trans("Date"),
      dataIndex: "date",
      width: 160,
      sorter: true,
      fixed: !isRTL ? "left" : undefined,
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: trans("Store"),
      dataIndex: "store",
      width: 150,
      sorter: true,
      filters: storeOptions,
      filterMultiple: false,
      onFilter: () => true,
      filteredValue: filters.storeId ? [filters.storeId] : null,
    },
    {
      title: trans("State"),
      key: "stateGeo",
      dataIndex: "stateGeo",
      width: 130,
      filters: stateOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableStates,
      onFilter: () => true,
      render: (val) =>
        val ? (
          <Tag color="blue" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("City"),
      key: "city",
      dataIndex: "city",
      width: 130,
      filters: cityOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableCities,
      onFilter: () => true,
      render: (val) =>
        val ? (
          <Tag color="cyan" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Sales Rep"),
      dataIndex: "rep",
      width: 150,
      filters: repOptions,
      filterMultiple: false,
      onFilter: () => true,
      filteredValue: filters.repId ? [filters.repId] : null,
    },
    {
      title: trans("SKU"),
      dataIndex: "sku",
      width: 180,
      filters: productOptions,
      filterMultiple: false,
      onFilter: () => true,
      filteredValue: filters.sku ? [filters.sku] : null,
    },
    {
      title: trans("Category"),
      key: "category",
      dataIndex: "category",
      width: 150,
      filters: categoryOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableCategories,
      onFilter: () => true,
      render: (cat) =>
        cat ? (
          <Tag color="purple" style={{ borderRadius: 10, fontWeight: 500 }}>
            {isRTL ? cat.name : (cat.nameEn || cat.name)}
          </Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Location Type"),
      key: "storeType",
      dataIndex: "storeType",
      width: 140,
      filters: storeTypeOptions,
      filterMultiple: true,
      filteredValue: tableStoreTypes,
      onFilter: () => true,
      render: (val) =>
        val ? (
          <Tag color="geekblue" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Case Pack"),
      dataIndex: "unitsPerContainer",
      width: 120,
    },
    {
      title: trans("State1"),
      dataIndex: "state",
      width: 90,
      sorter: true,
      render: (val) => <Tag color={STATE_COLORS[val] || "default"}>{val}</Tag>,
    },
    {
      title: trans("Shelf Status"),
      dataIndex: "shelfStatus",
      width: 130,
      render: (val) => (
        <Tag color={val === "موجود" ? "green" : "red"}>{val}</Tag>
      ),
    },
    {
      title: trans("Shelf Qty"),
      dataIndex: "shelfQty",
      width: 110,
      sorter: true,
    },
    {
      title: trans("Containers"),
      dataIndex: "shelfQtyContainers",
      width: 120,
      align: "center",
      render: (val) =>
        val != null ? (
          <span style={{ fontWeight: 600 }}>{val}</span>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Shelf Price"),
      dataIndex: "shelfPrice",
      width: 110,
      align: "center",
      render: (val) =>
        val != null ? (
          <span style={{ fontWeight: 600, color: "#389e0d" }}>{val}</span>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Warehouse Status"),
      dataIndex: "warehouseStatus",
      width: 150,
      render: (val) => (
        <Tag color={val === "موجود" ? "green" : "red"}>{val}</Tag>
      ),
    },
    {
      title: trans("Warehouse Qty"),
      dataIndex: "warehouseQty",
      width: 130,
      sorter: true,
      render: (val) => (val !== null && val !== undefined ? val : "—"),
    },
    {
      title: trans("Photo Before"),
      dataIndex: "photoBeforeUrl",
      width: 100,
      render: (url) =>
        url ? (
          <Image
            width={64}
            height={64}
            src={url}
            style={{ objectFit: "cover", borderRadius: 4 }}
            preview={{ mask: false }}
          />
        ) : (
          "—"
        ),
    },
    {
      title: trans("Photo After"),
      dataIndex: "photoAfterUrl",
      width: 100,
      render: (url) =>
        url ? (
          <Image
            width={64}
            height={64}
            src={url}
            style={{ objectFit: "cover", borderRadius: 4 }}
            preview={{ mask: false }}
          />
        ) : (
          "—"
        ),
    },
    {
      title: trans("Notes"),
      dataIndex: "notes",
      width: 180,
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <Typography.Text ellipsis style={{ maxWidth: 160, display: "block" }}>
              {val}
            </Typography.Text>
          </Tooltip>
        ) : (
          "—"
        ),
    },
  ];

  const handleTableChange = (pagination, tableFilters, sorter) => {
    if (sorter.order) {
      SetSortFilter({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      SetSortFilter({ by: "", order: "" });
    }

    const newStoreId      = tableFilters.store?.[0]      ?? undefined;
    const newRepId        = tableFilters.rep?.[0]        ?? undefined;
    const newSku          = tableFilters.sku?.[0]        ?? undefined;
    const newStoreTypes   = tableFilters.storeType       || null;
    const newCategories   = tableFilters.category        || null;
    const newStates       = tableFilters.stateGeo        || null;
    let   newCities       = tableFilters.city            || null;

    const arrEq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    // When the state selection changes, drop any city selection that no longer
    // belongs to the chosen state(s) so the two filters stay consistent.
    const stateChanged = !arrEq(newStates, filters.tableStates);
    if (stateChanged && newStates && newStates.length && newCities) {
      const allowed = (data?.filterOptions?.cities || [])
        .filter(c => newStates.includes(c.state))
        .map(c => c.value);
      newCities = newCities.filter(c => allowed.includes(c));
      if (!newCities.length) newCities = null;
    }

    if (
      newStoreId !== filters.storeId ||
      newRepId   !== filters.repId   ||
      newSku     !== filters.sku     ||
      !arrEq(newStoreTypes, filters.tableStoreTypes) ||
      !arrEq(newCategories, filters.tableCategories) ||
      !arrEq(newStates, filters.tableStates) ||
      !arrEq(newCities, filters.tableCities)
    ) {
      setFilters((prev) => ({
        ...prev,
        storeId:         newStoreId,
        repId:           newRepId,
        sku:             newSku,
        tableStoreTypes: newStoreTypes,
        tableCategories: newCategories,
        tableStates:     newStates,
        tableCities:     newCities,
      }));
    }
  };

  return (
    <div className="bg-white-rounded-top mt-3 table-parent">
      <AntdTable
        columns={columns}
        dataSource={data?.items || []}
        loading={isLoading}
        pageSize={limit}
        totalItems={data?.totalItems || 0}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setCurrentPage(1);
        }}
        onTableChange={handleTableChange}
        scrollY={350}
        scrollX
      />
    </div>
  );
};

export default ShelfAvailabilityReportTable;
